import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  static #globalCachedContent = new Map();
  /** @type {number} */
  static #MAX_CACHE_SIZE = 20;

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const productLink = productCard?.getProductCardLink();

    if (!productLink?.href) return '';

    const url = new URL(productLink.href);

    if (url.searchParams.has('variant')) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() || null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeDialogsOnBreakpointChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeDialogsOnBreakpointChange);
    this.#abortController?.abort();
    this.#abortController = null;
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    const currentUrl = this.productPageUrl;

    // Check if we have cached content for this URL in global cache
    let productGrid = QuickAddComponent.#globalCachedContent.get(currentUrl);

    if (!productGrid) {
      // Fetch and cache the content
      const html = await this.fetchProductPage(currentUrl);
      if (html) {
        const gridElement = html.querySelector('[data-product-grid-content]');
        if (gridElement) {
          // Cache the cloned element to avoid modifying the original
          productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
          
          // Limit cache size
          if (QuickAddComponent.#globalCachedContent.size >= QuickAddComponent.#MAX_CACHE_SIZE) {
            const firstKey = QuickAddComponent.#globalCachedContent.keys().next().value;
            QuickAddComponent.#globalCachedContent.delete(firstKey);
          }
          
          QuickAddComponent.#globalCachedContent.set(currentUrl, productGrid);
        }
      }
    }

    if (productGrid) {
      // Use a fresh clone from the cache
      const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
      await this.updateQuickAddModal(freshContent);
    }

    this.#openQuickAddModal();
  };

  /**
   * Handles B2B quick order button click
   * @param {Event} event - The click event
   */
  handleB2BQuickOrderClick = async (event) => {
    event.preventDefault();

    const currentUrl = this.productPageUrl;
    if (!currentUrl) return;

    try {
      await import('@theme/quick-order-list');
      const html = await this.fetchProductPage(currentUrl);
      if (!html) return;

      const quickOrderComponent = html.querySelector('quick-order-list-component');
      let quickOrderSection = null;
      if (quickOrderComponent) {
        const closestSection = quickOrderComponent.closest('[id^="shopify-section-"]');
        if (closestSection) {
          quickOrderSection = /** @type {Element} */ (closestSection.cloneNode(true));
        }
      }

      if (quickOrderSection) {
        const freshContent = /** @type {Element} */ (quickOrderSection.cloneNode(true));
        this.#prepareB2BQuickOrderContent(freshContent, currentUrl);
        await this.updateB2BQuickOrderModal(freshContent);
        this.#openB2BQuickOrderModal();
        return;
      }
    } catch (error) {
      console.error(error);
    }

    const fallbackUrl = new URL(currentUrl, window.location.origin);
    fallbackUrl.hash = 'bm-quick-order-list';
    window.location.assign(fallbackUrl.toString());
  };

  /** @param {DialogComponent} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  #openB2BQuickOrderModal = () => {
    const dialogComponent = document.getElementById('b2b-quick-order-dialog');
    if (!(dialogComponent instanceof B2BQuickOrderDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();
  };

  #closeB2BQuickOrderModal = () => {
    const dialogComponent = document.getElementById('b2b-quick-order-dialog');
    if (!(dialogComponent instanceof B2BQuickOrderDialog)) return;

    dialogComponent.closeDialog();
  };

  #closeDialogsOnBreakpointChange = () => {
    this.#closeQuickAddModal();
    this.#closeB2BQuickOrderModal();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    if (isMobileBreakpoint()) {
      const productDetails = productGrid.querySelector('.product-details');
      if (!productDetails) return;
      const productFormComponent = productGrid.querySelector('product-form-component');
      const variantPicker = productGrid.querySelector('variant-picker');
      const productPrice = productGrid.querySelector('product-price');
      const productTitle = document.createElement('a');
      productTitle.textContent = this.dataset.productTitle || '';

      // Make product title as a link to the product page
      productTitle.href = this.productPageUrl;

      if (!productFormComponent || !variantPicker || !productPrice || !productTitle) return;

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');

      productHeader.appendChild(productTitle);
      productHeader.appendChild(productPrice);
      productGrid.appendChild(productHeader);
      productGrid.appendChild(variantPicker);
      productGrid.appendChild(productFormComponent);
      productDetails.remove();
    }

    this.#removeSpecCompareWidgets(productGrid);

    morph(modalContent, productGrid);

    this.#syncVariantSelection(modalContent);
  }

  /**
   * Re-renders B2B quick order list modal content.
   * @param {Element} sectionElement
   */
  async updateB2BQuickOrderModal(sectionElement) {
    const modalContent = document.getElementById('b2b-quick-order-modal-content');

    if (!sectionElement || !modalContent) return;

    modalContent.replaceChildren();
    modalContent.appendChild(sectionElement);
  }

  /**
   * Prepares quick order content extracted from a product page before injecting into modal.
   * @param {Element} sectionElement
   * @param {string} productPageUrl
   */
  #prepareB2BQuickOrderContent(sectionElement, productPageUrl) {
    for (const script of sectionElement.querySelectorAll('script')) {
      script.remove();
    }

    if (!sectionElement.classList.contains('shopify-section')) {
      sectionElement.classList.add('shopify-section');
    }

    const quickOrderComponent = sectionElement.querySelector('quick-order-list-component');
    if (!(quickOrderComponent instanceof HTMLElement)) return;

    const sectionId = quickOrderComponent.dataset.sectionId;
    if (!sectionId) return;

    sectionElement.id = `shopify-section-${sectionId}`;

    const pageUrl = new URL(productPageUrl, window.location.origin);
    quickOrderComponent.dataset.sectionsUrl = `${pageUrl.pathname}${pageUrl.search}`;
  }

  /**
   * Removes Spec Compare app markup from quick add content.
   * The app component throws in quick-add modal re-renders, so we skip it here.
   * @param {Element} productGrid
   */
  #removeSpecCompareWidgets(productGrid) {
    const selectors = [
      '.sc-app-block',
      'spec-and-compare',
      'specs-and-comare',
      '[id*="spec_compare"]',
      '[class*="spec_compare"]',
      'script[src*="spec-and-compare"]',
      'script[src*="specs-and-comare"]',
    ];

    for (const node of productGrid.querySelectorAll(selectors.join(','))) {
      node.remove();
    }
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}

class B2BQuickOrderDialog extends DialogComponent {
  #abortController = new AbortController();
  #noticeTimer;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(DialogCloseEvent.eventName, this.#hideNotice);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#hideNotice);
    window.clearTimeout(this.#noticeTimer);
  }

  /**
   * Shows a temporary cart update notice in the B2B quick order modal.
   * @param {CartUpdateEvent} event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;

    const message = event.detail.data.quickOrderNotice || 'Cart updated.';
    this.#showNotice(message);
  };

  /**
   * @param {string} message
   */
  #showNotice(message) {
    const notice = this.querySelector('[data-b2b-quick-order-notice]');
    if (!(notice instanceof HTMLElement)) return;

    window.clearTimeout(this.#noticeTimer);
    notice.setAttribute('aria-hidden', 'false');
    notice.textContent = message;
    notice.classList.add('is-visible');

    this.#noticeTimer = window.setTimeout(() => {
      this.#hideNotice();
    }, 3500);
  }

  #hideNotice = () => {
    const notice = this.querySelector('[data-b2b-quick-order-notice]');
    if (!(notice instanceof HTMLElement)) return;

    window.clearTimeout(this.#noticeTimer);
    notice.classList.remove('is-visible');
    notice.setAttribute('aria-hidden', 'true');

    this.#noticeTimer = window.setTimeout(() => {
      if (!notice.classList.contains('is-visible')) notice.textContent = '';
    }, 180);
  };
}

if (!customElements.get('b2b-quick-order-dialog')) {
  customElements.define('b2b-quick-order-dialog', B2BQuickOrderDialog);
}
